import React, { useCallback, useEffect } from 'react';

import Validate from 'validate.js';

import connectAuth, { IAuthMapStateToProps, IConnectedProps } from '../../../hooks/connectAuth';

import Select, { Option } from '../../../../Common/Form/Select';
import { IAuthState } from '../../../Store/Reducers';
import { IInputData, Types } from '../../../Store/SignUpReducer';


interface IInputGender {
    gender: IInputData,
    checkField: string,
    useUSFormat: boolean,
    referencing: IInputData,
    customGenderPlaceholder?: string
    customReferencePlaceholder?: string
}

type TInputGender = IConnectedProps<IInputGender>;

const GENDER = {
    male: '1',
    female: '0',
    notBinary: '2'
};

const REFERENCING = {
    [GENDER.female]: 'F',
    [GENDER.male]: 'M'
};

const VALIDATE_FIELDS = ['gender', 'referencing'];

const InputGender: React.FC<TInputGender> = ({ intl, dispatch, ...props }) => {
    const {
        gender,
        checkField,
        useUSFormat,
        referencing,
        customGenderPlaceholder,
        customReferencePlaceholder
    } = props;

    const genderPlaceholder = customGenderPlaceholder || intl.formatMessage({ id: 'authFunctional.personalData.placeholders.gender' });

    const referencePlaceholder = customReferencePlaceholder || intl.formatMessage({ id: 'authFunctional.personalData.placeholders.genderReference' });

    const validateField = useCallback((field: string) => {
        dispatch({
            type: Types.CHANGE_PERSONAL_FIELD_DATA,
            field,
            data: (() => {
                const fields: Record<string, IInputData> = { gender, referencing };

                const fieldsRules: Record<string, { presence: { message: string } }> = {
                    gender: {
                        presence: {
                            message: intl.formatMessage({ id: 'authFunctional.errors.genderEmpty' })
                        }
                    },

                    referencing: {
                        presence: {
                            message: intl.formatMessage({ id: 'authFunctional.errors.genderReferenceEmpty' })
                        }
                    }
                };

                const isValid = Validate({ [field]: fields[field].value }, { [field]: fieldsRules[field] });

                if (!!isValid && fields[field].touched) return { error: isValid[field][0] };

                return { value: fields[field].value };
            })()
        });
    }, [dispatch, gender, intl, referencing]);

    const handleChangeGenderReference = (value: string) => dispatch({
        type: Types.CHANGE_PERSONAL_FIELD_DATA,
        field: 'referencing',
        data: {
            value,
            error: ''
        }
    });

    const handleChangeGender = (value: string) => dispatch({
        type: Types.CHANGE_PERSONAL_DATA,
        data: {
            checkField: 'gender',
            gender: {
                value,
                error: ''
            }
        }
    });

    const handleFocusGender = () => {
        if (gender.touched) return;

        dispatch({
            type: Types.CHANGE_PERSONAL_FIELD_DATA,
            field: 'gender',
            data: { touched: true }
        });
    };

    const handleBlurGender = () => {
        if (!gender.touched || gender.value) return;

        validateField('gender');
    };

    const handleFocusReferencing = () => {
        if (referencing.touched) return;

        dispatch({
            type: Types.CHANGE_PERSONAL_FIELD_DATA,
            field: 'referencing',
            data: { touched: true }
        });
    };

    const handleBlurReferencing = () => validateField('referencing');

    useEffect(() => {
        if (!checkField || VALIDATE_FIELDS.indexOf(checkField) === -1) return;

        validateField(checkField);
    }, [checkField, validateField]);

    const renderReferencing = gender.value === GENDER.notBinary && !useUSFormat;

    return (
        <>
            <Select
                order={5}
                id="auth-gender"
                value={gender.value}
                error={gender.error}
                onBlur={handleBlurGender}
                onFocus={handleFocusGender}
                placeholder={genderPlaceholder}
                onValueChange={handleChangeGender}
            >
                <Option value={GENDER.female}>{intl.formatMessage({ id: 'authFunctional.personalData.genderOptions.female' })}</Option>
                <Option value={GENDER.male}>{intl.formatMessage({ id: 'authFunctional.personalData.genderOptions.male' })}</Option>
                <Option value={GENDER.notBinary}>{intl.formatMessage({ id: 'authFunctional.personalData.genderOptions.notBinary' })}</Option>
            </Select>

            { renderReferencing && (
                <Select
                    order={6}
                    value={referencing.value}
                    error={referencing.error}
                    id="auth-gender-reference"
                    onBlur={handleBlurReferencing}
                    onFocus={handleFocusReferencing}
                    placeholder={referencePlaceholder}
                    onValueChange={handleChangeGenderReference}
                >
                    <Option value="M">{intl.formatMessage({ id: 'authFunctional.personalData.genderReference.he' })}</Option>
                    <Option value="F">{intl.formatMessage({ id: 'authFunctional.personalData.genderReference.she' })}</Option>
                </Select>
            )}
        </>
    );
};

const mapStateToProps: IAuthMapStateToProps<IInputGender> = (state, isComparison = false) => ((!isComparison)
    ? {
        gender: (state as IAuthState).SignUp.personalData.gender,
        useUSFormat: (state as IAuthState).Container.i18n.useUSFormat,
        checkField: (state as IAuthState).SignUp.personalData.checkField,
        referencing: (state as IAuthState).SignUp.personalData.referencing,
        customGenderPlaceholder: (state as IAuthState).Container.communication.placeholderGender,
        customReferencePlaceholder: (state as IAuthState).Container.communication.placeholderReference
    } :
    {
        gender: state.gender,
        checkField: state.checkField,
        useUSFormat: state.useUSFormat,
        referencing: state.referencing,
        customGenderPlaceholder: state.customGenderPlaceholder,
        customReferencePlaceholder: state.customReferencePlaceholder
    }
);

export { GENDER, REFERENCING };
export default connectAuth(mapStateToProps)(InputGender);
